import {
  BodyText,
  Button,
  Callout,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useParams } from "@tanstack/react-router";
import { CirclePlus } from "lucide-react";
import { Fragment, useState } from "react";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { ImmunizationForm } from "./components/ImmunizationForm";
import { ImmunizationItem } from "./components/ImmunizationItem";
import { ImmunizationSkeleton } from "./components/ImmunizationSkeleton";
import { useAddImmunization, useGetImmunization } from "./network";

export const Immunizations = () => {
  const { patientId } = useParams({ strict: false });
  const [isAddingImmunization, setIsAddingImmunization] = useState(false);
  const {
    data,
    isLoading: isLoadingImmunization,
    isError,
    refetch,
  } = useGetImmunization({ patientId: patientId as string });
  const { mutate: addImmunization, isPending: isSubmitting } =
    useAddImmunization({
      patientId: patientId as string,
      onSuccess: () => {
        toast.success("Immunization added successfully");
        setIsAddingImmunization(false);
      },
      onError: (error) => {
        toast.error(`Failed to add immunization: ${error.message}`);
      },
    });

  const showSkeleton = isLoadingImmunization;
  const showError = !showSkeleton && (isError || !data);
  const showEmpty =
    !showSkeleton &&
    !showError &&
    !isAddingImmunization &&
    data &&
    data.immunizations.length === 0;
  const showImmunizations =
    !showSkeleton && !showError && data && data.immunizations.length > 0;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Immunizations"
        actionButtons={[
          <Tooltip tooltipContentText="Add" side="bottom" key="add">
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => {
                trackEvent(TRACKERS.ADD_IMMUNIZATION);
                setIsAddingImmunization(true);
              }}
              aria-label="Add immunization"
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {isAddingImmunization && (
          <>
            <ImmunizationForm
              onCancel={() => {
                trackEvent(TRACKERS.CANCEL_IMMUNIZATION);
                setIsAddingImmunization(false);
              }}
              onSubmit={(data) => {
                trackEvent(TRACKERS.SAVE_IMMUNIZATION);
                addImmunization({
                  patientId: patientId as string,
                  dateAdministered: data.dateAdministered.toISOString(),
                  vaccineCode: {
                    coding: [
                      {
                        code: data.vaccineCode,
                        display: data.vaccineDisplay,
                        system: data.vaccineSystem,
                      },
                    ],
                    text: data.vaccineDisplay,
                  },
                });
              }}
              isSubmitting={isSubmitting}
            />
            <Separator className="w-full" />
          </>
        )}
        {showSkeleton && <ImmunizationSkeleton />}
        {showError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load immunizations!"
              description="Please click here to try again."
              onClick={() => refetch()}
            />
          </div>
        )}
        {showEmpty && (
          <div className="flex py-2 w-full">
            <BodyText size="sm" textColor="tertiary">
              No immunizations documented
            </BodyText>
          </div>
        )}
        {showImmunizations && (
          <div>
            {data.immunizations.map((immunization) => (
              <Fragment key={immunization.id}>
                <ImmunizationItem
                  key={immunization.id}
                  immunization={immunization}
                />
                <Separator className="w-full" />
              </Fragment>
            ))}
          </div>
        )}
      </SectionContent>
    </div>
  );
};
